import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  useTheme,
} from "@mui/material";
import { ArrowRight } from "lucide-react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CustomSwitch } from "../../../components/UI/ToggleSection";
import { useSettings } from "../../../context/SettingsContext";

export default function SettingsSLA({ value = 30 }) {
  const theme = useTheme();
  const { maxSla, updateSla } = useSettings();

  const initial = maxSla ?? value;

  const [unit, setUnit] = useState("minutes");
  const [amount, setAmount] = useState(initial);
  const [original, setOriginal] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [notifyManager, setNotifyManager] = useState(true);
  const [autoReassign, setAutoReassign] = useState(false);

  const minutesValue = useMemo(() => {
    if (unit === "hours") return Number(amount) * 60;
    if (unit === "days") return Number(amount) * 1440;
    return Number(amount);
  }, [unit, amount]);

  const displayValue = amount || 0;
  const changed = minutesValue !== original;

  const minuteOptions = [10, 20, 30, 40];
  const hourOptions = [0.5, 1, 2, 4, 8];
  const dayOptions = [1, 2, 3, 5, 7];

  const options =
    unit === "minutes"
      ? minuteOptions
      : unit === "hours"
      ? hourOptions
      : dayOptions;

  const save = async () => {
    try {
      setSaving(true);
      await updateSla(minutesValue);
      setOriginal(minutesValue);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setUnit("minutes");
    setAmount(original);
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      {/* Set Response Target Card */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          bgcolor: "#fff",
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 700,
            color: "#1e293b",
            textAlign: "center",
            mb: 0.5,
          }}
        >
          Set Response Target
        </Typography>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: theme.palette.primary.main,
            textAlign: "center",
            mb: 3,
          }}
        >
          Configure the default response time for incoming leads.
        </Typography>

        {/* Current → New value display */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} mb={3}>
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
              textAlign: "center",
              minWidth: 100,
            }}
          >
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#1e293b" }}>
              {original || displayValue}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {unit}
            </Typography>
          </Box>

          <ArrowRight size={20} color="#cbd5e1" />

          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: "10px",
              bgcolor: theme.palette.primary.main,
              textAlign: "center",
              minWidth: 100,
            }}
          >
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#fff" }}>
              {displayValue}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.8)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {unit}
            </Typography>
          </Box>
        </Stack>

        {/* Unit toggle: MINUTES / HOURS / DAYS */}
        <Stack alignItems="center" mb={2.5}>
          <ToggleButtonGroup
            exclusive
            value={unit}
            onChange={(_, v) => {
              if (v) {
                setAmount(0);
                setUnit(v);
              }
            }}
            sx={{
              bgcolor: "#f1f5f9",
              borderRadius: "8px",
              p: 0.4,
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "6px !important",
                px: 2,
                py: 0.5,
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                "&.Mui-selected": {
                  bgcolor: "#fff",
                  color: "#1e293b",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                },
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.5)",
                },
              },
            }}
          >
            <ToggleButton value="minutes">Minutes</ToggleButton>
            <ToggleButton value="hours">Hours</ToggleButton>
            <ToggleButton value="days">Days</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Quick-select chips */}
        <Stack direction="row" spacing={1.5} justifyContent="center">
          {options.map((v) => (
            <Box
              key={v}
              onClick={() => setAmount(v)}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.875rem",
                bgcolor: amount === v ? theme.palette.primary.main : "transparent",
                color: amount === v ? "#fff" : "#1e293b",
                border: amount === v ? "none" : "1px solid #e2e8f0",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: amount === v ? theme.palette.primary.main : "#f1f5f9",
                },
              }}
            >
              {unit === "hours" ? `${v}h` : unit === "days" ? `${v}d` : v}
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Breach Escalation Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          bgcolor: "#fff",
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
          <WarningAmberIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
            Breach Escalation
          </Typography>
        </Stack>

        {/* Notify Manager */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
              Notify Manager
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              Send an immediate alert when a target is missed
            </Typography>
          </Box>
          <CustomSwitch
            checked={notifyManager}
            onChange={() => setNotifyManager((p) => !p)}
          />
        </Box>

        {/* Auto-Reassign Lead */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
              Auto-Reassign Lead
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              Move lead to the next available agent on breach
            </Typography>
          </Box>
          <CustomSwitch
            checked={autoReassign}
            onChange={() => setAutoReassign((p) => !p)}
          />
        </Box>
      </Paper>

      {/* Discard Changes */}
      {changed && (
        <Stack direction="row" justifyContent="flex-end">
          <Typography
            onClick={cancel}
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#64748b",
              cursor: "pointer",
              "&:hover": { color: "#1e293b" },
            }}
          >
            Discard Changes
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
